import { Controller } from "@hotwired/stimulus"

// Mirrors ColorConverter::WCAG_LUMINANCE_THRESHOLD (0.179) from app/lib/color_converter.rb.
// This threshold determines white vs dark text on colored backgrounds per WCAG AA.
// The server-side Ruby version is authoritative; this JS copy provides instant preview feedback.
const WCAG_LUMINANCE_THRESHOLD = 0.179
const LIGHT_TEXT = "#FFFFFF"
const DARK_TEXT = "#1A1A2E"

export default class extends Controller {
  static targets = [
    "primaryColor", "primaryHex", "secondaryColor", "secondaryHex",
    "fontFamily", "footerText",
    "sidebarStrip", "primaryBtn", "secondaryBtn", "navItem",
    "previewHeading", "previewFooter", "previewOrgName",
    "logoError"
  ]

  update() {
    const primary = this.primaryColorTarget.value
    const secondary = this.secondaryColorTarget.value
    const font = this.fontFamilyTarget.value

    // Sync hex inputs with color pickers
    this.primaryHexTarget.value = primary
    this.secondaryHexTarget.value = secondary

    this._applyPreview(primary, secondary, font)
  }

  updateFromHex() {
    const primary = this.primaryHexTarget.value
    const secondary = this.secondaryHexTarget.value

    // Validate hex format before syncing
    if (/^#[0-9A-Fa-f]{6}$/.test(primary)) {
      this.primaryColorTarget.value = primary
    }
    if (/^#[0-9A-Fa-f]{6}$/.test(secondary)) {
      this.secondaryColorTarget.value = secondary
    }

    const font = this.fontFamilyTarget.value
    this._applyPreview(
      /^#[0-9A-Fa-f]{6}$/.test(primary) ? primary : this.primaryColorTarget.value,
      /^#[0-9A-Fa-f]{6}$/.test(secondary) ? secondary : this.secondaryColorTarget.value,
      font
    )
  }

  _applyPreview(primary, secondary, font) {
    // Sidebar strip
    if (this.hasSidebarStripTarget) {
      this.sidebarStripTarget.style.backgroundColor = primary
    }

    // Buttons
    if (this.hasPrimaryBtnTarget) {
      this.primaryBtnTarget.style.backgroundColor = primary
      this.primaryBtnTarget.style.color = this._contrastColor(primary)
    }
    if (this.hasSecondaryBtnTarget) {
      this.secondaryBtnTarget.style.backgroundColor = secondary
      this.secondaryBtnTarget.style.color = this._contrastColor(secondary)
    }

    // Nav item
    if (this.hasNavItemTarget) {
      this.navItemTarget.style.borderColor = primary
      this.navItemTarget.style.backgroundColor = primary + "10"
      this.navItemTarget.style.color = primary
    }

    // Font
    if (this.hasPreviewHeadingTarget) {
      this.previewHeadingTarget.style.fontFamily = `'${font}', sans-serif`
    }

    // Footer text (safe — uses textContent to prevent XSS)
    if (this.hasFooterTextTarget && this.hasPreviewFooterTarget) {
      const footerVal = this.footerTextTarget.value
      const year = new Date().getFullYear()
      const displayText = footerVal || "Your Organization \u2014 Digital Operations Log System"
      this.previewFooterTarget.textContent = `\u00A9 ${year} ${displayText}`
    }
  }

  validateLogo(event) {
    const input = event.target
    const errorTarget = this.hasLogoErrorTarget ? this.logoErrorTarget : null

    if (errorTarget) errorTarget.textContent = ""

    if (!input.files || !input.files[0]) return

    const file = input.files[0]
    const allowedTypes = ["image/png", "image/jpeg", "image/svg+xml"]
    const allowedExtensions = [".png", ".jpg", ".jpeg", ".svg"]
    const maxSize = 2 * 1024 * 1024 // 2MB
    const fileExtension = file.name.toLowerCase().match(/\.[^.]+$/)?.[0] || ""

    if (!allowedTypes.includes(file.type) || !allowedExtensions.includes(fileExtension)) {
      input.value = ""
      if (errorTarget) {
        const p = document.createElement("p")
        p.className = "text-error text-xs mt-1"
        p.textContent = "Invalid file type. Please upload a PNG, JPG, or SVG file."
        errorTarget.appendChild(p)
      }
      return
    }

    if (file.size > maxSize) {
      input.value = ""
      if (errorTarget) {
        const p = document.createElement("p")
        p.className = "text-error text-xs mt-1"
        p.textContent = "File is too large. Maximum size is 2MB."
        errorTarget.appendChild(p)
      }
      return
    }
  }

  // Mirrors ColorConverter.contrast_color — WCAG luminance threshold 0.179
  _contrastColor(hex) {
    const r = parseInt(hex.slice(1, 3), 16) / 255
    const g = parseInt(hex.slice(3, 5), 16) / 255
    const b = parseInt(hex.slice(5, 7), 16) / 255

    const toLinear = (c) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
    const lum = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)

    return lum < WCAG_LUMINANCE_THRESHOLD ? LIGHT_TEXT : DARK_TEXT
  }
}
